import "./facilityDetailsAddComponent.scss";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import Grid from "@mui/material/Grid";
import FormikInputComponent from "../../../../shared/components/core/formik-input/FormikInputComponent";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import CommonService from "../../../../shared/services/common.service";
import * as Yup from "yup";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import FormikAutoCompleteDropdown
    from "../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import {APIConfig, ImageConfig} from "../../../../constants";
import _ from "lodash";
import commonService from "../../../../shared/services/common.service";
import {useSearchParams} from "react-router-dom";
import FormikRadioButtonGroupComponent
    from "../../../../shared/components/core/formik-radio-button-group/formikRadioButtonGroupComponent";

interface FacilityDetailsAddComponentProps {
    next: Function;
    cancel: Function;
    facilityDetails?: any;
    facilityId?: string;
}

const facilityAddressValidationSchema = Yup.object({
        addressLine1: Yup.string().nullable()
            .required("Address 1 is required")
            .max(200, 'Address Line 1 cannot exceed 200 characters'),
        addressLine2: Yup.string().nullable().max(200, 'Address Line 2 cannot exceed 200 characters'),
        zipcode: Yup.mixed().required("Zip Code is required"),
        state: Yup.mixed().required("State is required"),
        city: Yup.mixed().required("City is required"),
        newZipCode: Yup.string().when('zipcode', {
            is: (zipcode: any) => (zipcode?.zipcode && zipcode?.zipcode?.toLowerCase() === "other"),
            then: Yup.string().required('ZIP Code is required')
                .matches(/^[0-9]{5}$/, "Enter a valid ZIP Code"),
        }),
    }
);

interface facilityInformationAddressFormSchema {
    addressLine1: string,
    addressLine2: string,
    zipcode: any,
    newZipCode: string,
    state: string,
    city: string,
}

const facilityInformationAddressInitialValues: facilityInformationAddressFormSchema = {
    addressLine1: "",
    addressLine2: "",
    zipcode: {},
    newZipCode: "",
    state: "",
    city: "",
}
const facilityInformationValidationSchema = Yup.object({
        name: Yup.string().required("Facility Name is required"),
        facilityType: Yup.mixed().required("Facility Type is required"),
        isInsuranceImageRequired: Yup.boolean().nullable().required("Select Insurance Preferences"),
        addressDetails: facilityAddressValidationSchema
    }
);

interface facilityInformationFormSchema {
    name: string;
    facilityType: any;
    addressDetails: facilityInformationAddressFormSchema,
    isInsuranceImageRequired: boolean | null,
}

const facilityInformationInitialValues: facilityInformationFormSchema = {
    name: "",
    facilityType: "",
    addressDetails: facilityInformationAddressInitialValues,
    isInsuranceImageRequired: null,
}


const FacilityDetailsAddComponent = (props: FacilityDetailsAddComponentProps) => {
    const {next, cancel, facilityDetails} = props;

    const [facilityDetailsInitialValues, setFacilityDetailsInitialvalues] = useState<facilityInformationFormSchema>(facilityInformationInitialValues);

    const [isFacilityUnique, setFacilityUnique] = useState<boolean | undefined>(undefined);
    const {facilityTypesList} = useSelector((state: IRootReducerState) => state.staticData);
    const [state, setState] = useState<string>("");
    const [isZipCodeNotFound, setIsZipCodeNotFound] = useState<boolean>(false);
    const APICallSubscription = useRef<any>(null);
    const formikRef = useRef<any>(null);
    const [searchParams] = useSearchParams();
    const lastCompletedStep = searchParams.get("lastCompletedStep");

    const handleZipCodeValueChange = useCallback((zipCodeObject: any, setFieldValue: any) => {
        if (zipCodeObject === "") {
            setFieldValue("addressDetails.state", "");
            setFieldValue("addressDetails.city", "");
            setIsZipCodeNotFound(false);
            setState("");
        } else {
            if (zipCodeObject?.zipcode?.toLowerCase() === "other") {
                setIsZipCodeNotFound(true);
                setFieldValue("addressDetails.state", "");
                setFieldValue("addressDetails.city", "");
            } else {
                setIsZipCodeNotFound(false);
                setFieldValue("addressDetails.state", zipCodeObject.state);
                setFieldValue("addressDetails.city", zipCodeObject.city);
            }
        }
        setFieldValue("newZipCode", "");
    }, []);


    const facilityVerify = useCallback((name: any, type: any, setFieldError: any) => {
        const cancelTokenSource = CommonService.getCancelToken();
        const payload = {
            name: name,
            type: type
        };
        let apiCall;
        apiCall = CommonService._api.post;
        if (APICallSubscription && APICallSubscription.current) {
            APICallSubscription.current.cancel();
        }
        setFacilityUnique(undefined);
        apiCall(APIConfig.FACILITY_VERIFY.URL, payload, {}, {cancelToken: cancelTokenSource.token}).then((response: IAPIResponseType<any>) => {
            if (response.data) {
                setFacilityUnique(response.data.isUnique);
                !response.data.isUnique && setFieldError("name", "Facility with the given name already exists! Please try another name.");
            }
        }).catch((error: any) => {

        });
    }, []);

    const handleFacilityAddSuccess = useCallback((values: any, {
        setSubmitting,
        setErrors,
        validateForm
    }: FormikHelpers<any>) => {
        if (!facilityDetails) {
            CommonService.openConfirmationDialog({
                confirmationTitle: 'Please confirm!',
                confirmationSubTitle: 'Are you sure you want to continue adding this facility?',
                // confirmationDescription: 'You won\'t be able to update this later in the current flow, but you can edit it from the Facility page later.',
                disableEscKeyClose: false,
                disableBackdropClose: true,
                imagePath: `${ImageConfig.ConfirmationImage}`,
                direction: "up",
                yes: {
                    text: "Continue",
                    color: "primary"
                },
                no: {
                    text: "Cancel",
                    color: "primary"
                }
            }).then(() => {
                const facilityBasicInformationDetailsObj = {...values};
                if (facilityBasicInformationDetailsObj.addressDetails["newZipCode"]) {
                    facilityBasicInformationDetailsObj.addressDetails["zipcode"] = facilityBasicInformationDetailsObj.addressDetails["newZipCode"];
                    facilityBasicInformationDetailsObj.addressDetails["state"] = facilityBasicInformationDetailsObj.addressDetails?.state?.state;
                    facilityBasicInformationDetailsObj.addressDetails["city"] = facilityBasicInformationDetailsObj.addressDetails?.city?.city;
                    delete facilityBasicInformationDetailsObj.addressDetails["newZipCode"];
                } else {
                    facilityBasicInformationDetailsObj.addressDetails["zipcode"] = facilityBasicInformationDetailsObj.addressDetails?.zipcode?.zipcode;
                }
                facilityBasicInformationDetailsObj.type = facilityBasicInformationDetailsObj.facilityType.code;
                const payload = {...facilityBasicInformationDetailsObj};
                setSubmitting(true);
                CommonService._facility.addFacility(payload)
                    .then((response: IAPIResponseType<any>) => {
                        setSubmitting(false);
                        next(response.data.id, response.data);
                    })
                    .catch((error: any) => {
                        CommonService.handleErrors(setErrors, error);
                        setSubmitting(false);
                    })
            }).catch(() => {
                setSubmitting(false);
                // do nothing;
            });
        } else {
            if (formikRef?.current?.dirty) {
                const payload = _.cloneDeep({...values, lastCompletedStep: lastCompletedStep});
                payload.type = payload.facilityType.code
                if (payload.addressDetails["newZipCode"]) {
                    payload.addressDetails["zipcode"] = payload.addressDetails["newZipCode"];
                    payload.addressDetails["state"] = payload.addressDetails?.state?.state;
                    payload.addressDetails["city"] = payload.addressDetails?.city?.city;
                    delete payload.addressDetails["newZipCode"];
                } else {
                    payload.addressDetails["zipcode"] = payload.addressDetails?.zipcode?.zipcode;
                }
                setSubmitting(true);
                CommonService._facility.editFacilityDetails(facilityDetails?.id, payload)
                    .then((response: IAPIResponseType<any>) => {
                        setSubmitting(false);
                        next(response.data.id, response.data);
                    })
                    .catch((error: any) => {
                        CommonService.handleErrors(setErrors, error);
                        setSubmitting(false);
                    });
            } else {
                setSubmitting(false);
                next(facilityDetails?.id, facilityDetails);
            }
        }
    }, [next, facilityDetails, formikRef, lastCompletedStep]);

    useEffect(() => {
        if (facilityDetails) {
            const facilityInformationObj = {...facilityDetails};
            const facilityInformationDetails: any = {addressDetails: {}};
            facilityInformationDetails['facilityType'] = {
                title: commonService.codeToTitleConverter(facilityInformationObj?.type),
                code: facilityInformationObj?.type
            };
            facilityInformationDetails['isInsuranceImageRequired'] = facilityInformationObj?.isInsuranceImageRequired;
            facilityInformationDetails['name'] = facilityInformationObj?.name;
            facilityInformationDetails.addressDetails.addressLine1 = facilityInformationObj?.addressDetails?.addressLine1;
            facilityInformationDetails.addressDetails.addressLine2 = facilityInformationObj?.addressDetails?.addressLine2;

            if (!facilityInformationObj.addressDetails.zipcodeDetails) {
                facilityInformationDetails.addressDetails.newZipCode = facilityInformationObj?.addressDetails?.zipcode;
                facilityInformationDetails.addressDetails.zipcode = {
                    zipcode: "Other"
                };
                facilityInformationDetails.addressDetails.state = facilityInformationObj?.addressDetails?.stateDetails;
                facilityInformationDetails.addressDetails.city = facilityInformationObj?.addressDetails?.cityDetails;
                setIsZipCodeNotFound(true);
            } else {
                facilityInformationDetails.addressDetails.zipcode = facilityInformationObj?.addressDetails?.zipcodeDetails;
                facilityInformationDetails.addressDetails.state = facilityInformationObj?.addressDetails?.stateDetails?.state;
                facilityInformationDetails.addressDetails.city = facilityInformationObj?.addressDetails?.cityDetails?.city;
                setIsZipCodeNotFound(false);
            }
            setFacilityUnique(true);
            setState(facilityInformationObj.addressDetails?.state);
            setFacilityDetailsInitialvalues(facilityInformationDetails);
        }
    }, [facilityDetails])


    return (
        <div className={'facility-details-add-component t-form-screen'}>
            <FormTitleWithNavigationBack
                title={'Facility Details'}
                tooltipData={"Ensure that the name of the Facility is unique. By entering a ZIP Code, the state and city will be auto populated."}
            />

            <div className="t-form">
                <Formik
                    validationSchema={facilityInformationValidationSchema}
                    initialValues={facilityDetailsInitialValues}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    innerRef={formikRef}
                    validateOnMount={true}
                    onSubmit={handleFacilityAddSuccess}
                >
                    {({isSubmitting, values, setFieldError, errors, isValid, setFieldValue, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            // eslint-disable-next-line no-script-url
                            <Form noValidate={true} action={'javascript:void'}>
                                <div className={"t-form-controls-wrapper"}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'facilityType'}>
                                                {
                                                    (field: FieldProps) => (

                                                        <FormikAutoCompleteDropdown
                                                            fullWidth={true}
                                                            label={'Facility Type'}
                                                            required={true}
                                                            multiple={false}
                                                            data={facilityTypesList}
                                                            onUpdate={(value: any) => {
                                                                if (values?.name)
                                                                    facilityVerify(values?.name, value?.code, setFieldError)
                                                            }
                                                            }
                                                            valueKey={"code"}
                                                            placeholder={'Select Facility Type'}
                                                            formikField={field}
                                                            getOptionLabel={(option => (option.title) || '')}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'name'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Facility Name'}
                                                            placeholder={'Enter Facility Name'}
                                                            type={"text"}
                                                            required={true}
                                                            disabled={!values.facilityType}
                                                            onUpdate={(value: any) => {
                                                                facilityVerify(value, values.facilityType?.code, setFieldError)
                                                            }
                                                            }
                                                            formikField={field}
                                                        />
                                                    )}
                                            </Field>
                                            {/*{*/}
                                            {/*    isFacilityUnique === false &&*/}
                                            {/*    <div className={"invalid-name-text"}>Facility with the given name already exists! Please try another*/}
                                            {/*        name.</div>*/}
                                            {/*}*/}
                                        </Grid>
                                    </Grid>

                                    <div>
                                        <div className="horizontal-line"/>
                                        <div className="form-sub-title">Address Details</div>
                                        <div>
                                            <Field name={'addressDetails.addressLine1'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Address Line 1'}
                                                            placeholder={'Enter Address Line 1'}
                                                            type={"text"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Grid container spacing={2}>

                                                <Grid item xs={12} sm={12} md={6} xl={6}>
                                                    <Field name={'addressDetails.addressLine2'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    label={'Address Line 2'}
                                                                    placeholder={'Enter Address Line 2'}
                                                                    type={"text"}
                                                                    formikField={field}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={6} xl={6}>
                                                    <Field name={'addressDetails.zipcode'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikAutoCompleteDropdown
                                                                    label={'ZIP Code'}
                                                                    placeholder={'Start typing for ZIP Code'}
                                                                    required={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                    openOnFocus={true}
                                                                    url={APIConfig.ZIPCODE_LIST.URL}
                                                                    method={APIConfig.ZIPCODE_LIST.METHOD}
                                                                    searchMode={'serverSide'}
                                                                    data={[]}
                                                                    valueKey={'zipcode'}
                                                                    dataListKey={"data.docs"}
                                                                    onUpdate={(value: any) => {
                                                                        handleZipCodeValueChange(value, setFieldValue)
                                                                    }}
                                                                    //Hiding other option from zipcode
                                                                    // defaultData={[
                                                                    //     {
                                                                    //         zipcode: "Other"
                                                                    //     }
                                                                    // ]}
                                                                    getOptionLabel={(option => (option.zipcode) || '')}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </Grid>
                                            </Grid>
                                            {isZipCodeNotFound &&
                                                <>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6}>
                                                            <Field name={'addressDetails.newZipCode'}>
                                                                {
                                                                    (field: FieldProps) => (
                                                                        <FormikInputComponent
                                                                            placeholder={"Enter New ZIP Code"}
                                                                            label={'New ZIP Code'}
                                                                            required={true}
                                                                            maxLength={5}
                                                                            type={"text"}
                                                                            formikField={field}
                                                                        />
                                                                    )
                                                                }
                                                            </Field>

                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Field name={'addressDetails.state'}>
                                                                {
                                                                    (field: FieldProps) => (
                                                                        <FormikAutoCompleteDropdown
                                                                            label={'State'}
                                                                            required={true}
                                                                            formikField={field}
                                                                            url={APIConfig.STATE_LIST.URL}
                                                                            method={APIConfig.STATE_LIST.METHOD}
                                                                            searchMode={'serverSide'}
                                                                            openOnFocus={true}
                                                                            dataListKey={"data.docs"}
                                                                            data={[]}
                                                                            valueKey={'state'}
                                                                            onUpdate={(value: any) => {
                                                                                setState(value?.state);
                                                                                field.form.setFieldValue("city", '');
                                                                            }}
                                                                            getOptionLabel={(option => (option.state) || '')}
                                                                        />
                                                                    )
                                                                }
                                                            </Field>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6}>
                                                            <Field name={'addressDetails.city'}>
                                                                {
                                                                    (field: FieldProps) => (
                                                                        <FormikAutoCompleteDropdown
                                                                            label={'City'}
                                                                            placeholder={'City'}
                                                                            required={true}
                                                                            id="city_auto_complete"
                                                                            formikField={field}
                                                                            searchMode={'serverSide'}
                                                                            url={APIConfig.CITY_LIST.URL}
                                                                            method={APIConfig.CITY_LIST.METHOD}
                                                                            valueKey={'city'}
                                                                            disabled={!state}
                                                                            payload={{
                                                                                state: state,
                                                                                page: 1,
                                                                                limit: 100
                                                                            }}
                                                                            data={[]}
                                                                            getOptionLabel={(option => (option.city) || '')}
                                                                            dataListKey={"data.docs"}
                                                                        />
                                                                    )
                                                                }
                                                            </Field>

                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>

                                                        </Grid>
                                                    </Grid>
                                                </>
                                            }
                                            {
                                                !isZipCodeNotFound && values?.addressDetails?.zipcode?.zipcode && values?.addressDetails?.zipcode?.zipcode !== "Other" &&
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'addressDetails.state'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'State'}
                                                                        placeholder={'Enter state'}
                                                                        required={true}
                                                                        readOnly={true}
                                                                        formikField={field}
                                                                        type={"text"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field name={'addressDetails.city'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'City'}
                                                                        placeholder={'Enter city'}
                                                                        required={true}
                                                                        readOnly={true}
                                                                        formikField={field}
                                                                        type={"text"}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </Grid>
                                                </Grid>
                                            }
                                        </div>
                                    </div>
                                    <section>
                                        <div className={'horizontal-line'}/>
                                        <div className="form-sub-title">Insurance Preference</div>
                                        <div className={'d-flex align-items-center'}>
                                            <div className='physician-address-same-as-facility-header'>
                                                Is uploading insurance images mandatory for this facility? *
                                            </div>
                                            <div className={'mrg-left-20'}>
                                                <Field name={'isInsuranceImageRequired'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikRadioButtonGroupComponent
                                                                direction={"row"}
                                                                formikField={field}
                                                                titleKey={'title'}
                                                                valueKey={'code'}
                                                                isValueBoolean={true}
                                                                onUpdate={(value: any) => {
                                                                }}
                                                                options={CommonService._staticData.yesNoOptions}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                                <div className="form-action-section">
                                    <ButtonComponent
                                        type="button"
                                        color={"primary"}
                                        variant={"outlined"}
                                        fullWidth={true}
                                        disabled={isSubmitting}
                                        handleClick={cancel}
                                    >
                                        Cancel
                                    </ButtonComponent>
                                    <ButtonComponent
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={true}
                                        disabled={!isValid || !isFacilityUnique}
                                        isLoading={isSubmitting}
                                    >
                                        Next
                                    </ButtonComponent>
                                </div>
                            </Form>
                        )
                    }
                    }
                </Formik>
            </div>
        </div>
    )
        ;

};

export default FacilityDetailsAddComponent;
